import { Component, inject, OnInit, signal } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';

import { CookieOptions, CookieService } from 'ngx-cookie';
import { EMPTY, zip } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { AUTH_COOKIES } from '@bn/auth/constants/cookie.constants';
import { AuthRoutes } from '@bn/auth/enums/auth-routes.enum';
import { RegisterSlide } from '@bn/auth/models/sliders/register-slide.model';
import { Trustpilot } from '@bn/auth/models/sliders/trustpilot.model';
import { AuthService } from '@bn/auth/services/auth.service';
import { AuthSliderService } from '@bn/auth/services/auth-slider.service';
import {
  APP_CONFIG,
  AppRoutes,
  AuthStatus,
  DateTimeUtils,
  EmailValidator,
  ENV_CONFIG,
  FormArrayValidator,
  sanitizeReturnUrl,
  SSOService,
  SSOUrlList,
  StringStringMap,
  UtmData,
} from '@bn/primitives';
import { UserAuth } from '@bn/shared/core/api';
import { MatomoService, PosthogService } from '@bn/shared/core/services';
import { MixpanelEventsDirective } from '@bn/shared/directives';
import { InputAction } from '@bn/web-ui/input/enums';
import { InputOptions } from '@bn/web-ui/input/models';

import { WHITE_LIST_RETURN_URL_DOMAINS } from '../login/login.page';

@Component({
  selector: 'bn-auth-register',
  templateUrl: './register.page.html',
  styleUrls: ['./register.page.scss'],
  host: { class: 'bn-auth__page-container' },
  standalone: false,
})
export class RegisterPage implements OnInit {
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #appConfig = inject(APP_CONFIG);
  readonly #authService = inject(AuthService);
  readonly #authSliderService = inject(AuthSliderService);
  readonly #cookie = inject(CookieService);
  readonly #envConfig = inject(ENV_CONFIG);
  readonly #matomo = inject(MatomoService);
  readonly #mixpanel = inject(MixpanelEventsDirective);
  readonly #posthog = inject(PosthogService);
  readonly #router = inject(Router);
  readonly #sanitizer = inject(DomSanitizer);

  captchaPassed = signal(false);
  domainInfo = '';
  powToken = signal('');
  unsafeDomainWarning = '';

  public tenantId = '';
  public versionId = '';
  public isLoading = {
    initial: true,
    submit: false,
  };
  public sliderConfig: RegisterSlide | null = null;
  public trustpilotConfig?: Trustpilot;
  public error?: string;
  public errorMessage?: string;
  readonly termsLabel = `I agree to the <a href="https://bunny.net/tos/" target="_blank"><strong>Terms of Service</strong></a>,
                      <a href="https://bunny.net/acceptable-use/" target="_blank"><strong>Acceptable Use Policy</strong></a>
                      and confirm that I have read and understood the bunny.net <a href="https://bunny.net/privacy/" target="_blank"><strong>Privacy Policy</strong></a>.`;
  readonly registerForm = new UntypedFormGroup({
    email: new UntypedFormControl('', [Validators.required, Validators.email, EmailValidator.emailDomain()]),
    password: new UntypedFormControl('', [FormArrayValidator.passwordPatternValidator(), Validators.required]),
    agreeTerms: new UntypedFormControl(undefined, [Validators.requiredTrue]),
  });
  public isExtAuthMode = false;
  public pageText: StringStringMap = { submit: 'Create an Account' };
  public wpQueries: StringStringMap = {};

  public readonly ssoUrlList: SSOUrlList = {};
  public readonly passwordInputSettings: Partial<InputOptions> = {
    rightAddons: [
      {
        icon: 'eye',
        action: InputAction.ShowPassword,
      },
    ],
    hideRequiredLabel: true,
  };

  constructor() {
    this.tenantId = this.#activatedRoute.snapshot.params?.tenantId ?? '';
    this.versionId = this.#activatedRoute.snapshot.queryParams?.ver ?? '';

    zip([this.#authSliderService.getTrustpilotData(), this.#authSliderService.getRegisterSlides(this.tenantId, this.versionId)]).subscribe({
      next: ([trustpilotConfig, registerConfig]) => {
        this.trustpilotConfig = trustpilotConfig ?? undefined;
        this.sliderConfig = registerConfig;

        this.isLoading.initial = false;
      },
      error: () => (this.isLoading.initial = false),
    });

    const { errorMessage, ref, returnUrl, token } = this.#activatedRoute.snapshot.queryParams;

    let selfUrl = returnUrl
      ? `${window.location.origin}${window.location.pathname}?returnUrl=${encodeURIComponent(sanitizeReturnUrl(returnUrl) ?? '')}`
      : `${window.location.origin}${window.location.pathname}`;

    selfUrl = encodeURIComponent(selfUrl);

    if (ref) this.#cookie.put(AUTH_COOKIES.AffiliateCode, String(ref));

    if (errorMessage) {
      this.setError('Error signing up', errorMessage);
    }

    this.ssoUrlList.google = `${this.#envConfig.apiUrl}/openid/${SSOService.Google}?callbackUrl=${selfUrl}&errorUrl=${selfUrl}`;
    this.ssoUrlList.gitlab = `${this.#envConfig.apiUrl}/openid/${SSOService.Gitlab}?callbackUrl=${selfUrl}&errorUrl=${selfUrl}`;
    this.ssoUrlList.github = `${this.#envConfig.apiUrl}/oauth/${SSOService.Github}?callbackUrl=${selfUrl}&errorUrl=${selfUrl}`;

    if (token) {
      this.tokenLogin(token);
    }
  }

  ngOnInit(): void {
    this.setTenantUtmData();

    const { callbackUrl, domain, source } = this.#activatedRoute.snapshot.queryParams;
    // TODO: make some kind of configuration with all of the texts, image assets and possible ?source values
    // TODO: don't duplicate logic in login.page
    if (source && domain && callbackUrl) {
      // came directly from auth referrer
      this.initExtAuth();
    }
  }

  public onSubmit(): void {
    if (this.registerForm.invalid || this.isLoading.submit) {
      return;
    }

    this.isLoading.submit = true;

    this.#authService
      .register(
        this.registerForm.controls.email.value,
        this.registerForm.controls.password.value,
        this.getUtmData(),
        this.#cookie.get(AUTH_COOKIES.AffiliateCode) || undefined,
        this.powToken(),
      )
      .pipe(
        switchMap(() => {
          this.isLoading.submit = false;
          // Cookie was used, remove it
          this.#cookie.remove(AUTH_COOKIES.AffiliateCode);

          return this.#authService.login(this.registerForm.controls.email.value, this.registerForm.controls.password.value).pipe(
            catchError(() => {
              this.#mixpanel.addEvent('Sign Up - Error', { type: 'Error' });

              this.#router.navigate([`/${AuthRoutes.Auth}/${AuthRoutes.Login}`]);
              return EMPTY;
            }),
          );
        }),
      )
      .subscribe({
        next: () => {
          this.#matomo.trackEvent('Registration', 'Sign Up - Email');
          this.#posthog.captureEvent('Sign Up', { type: 'Email' });
          this.#mixpanel.addEvent('Sign Up', { type: 'Email' });

          if (this.isExtAuthMode) {
            this.#authService.getApiKeyValue().subscribe((apiKey) => {
              if (!apiKey) return;

              const redirectUrl = this.#authService.getCallbackUrl(apiKey);

              // eslint-disable-next-line sonarjs/no-angular-bypass-sanitization
              this.#sanitizer.bypassSecurityTrustUrl(redirectUrl);
              this.#authService.removeCookieAndRedirect(redirectUrl);
            });
          } else {
            this.#authService.navigateAfterLogin(`/${AppRoutes.SimpleRegisterPoll}`).subscribe();
          }
        },
        error: (error) => {
          const apiError = error?.error;

          if (apiError?.Field?.toLowerCase() === 'email' && apiError?.Message) {
            this.registerForm.controls.email.setErrors({ apiResponse: { isError: true, message: apiError.Message } });
            this.registerForm.controls.email.markAsTouched();
          } else {
            this.setError('Error signing up', apiError?.Message || 'There was problem while trying to create your account.');
          }

          this.isLoading.submit = false;
        },
      });
  }

  private tokenLogin(token: string): void {
    // Note: "isWelcome" is set by google SSO when users register
    const isWelcome = this.#activatedRoute.snapshot.queryParams?.isWelcome === 'True';

    this.isLoading.submit = true;

    this.#authService.tokenLogin(token).subscribe((userAuth: UserAuth | false) => {
      if (!userAuth) {
        this.setError('Token not valid', 'There was a problem validating token. Try again or contact our support.');

        this.isLoading.submit = false;

        return;
      }

      if (userAuth.AuthStatus === AuthStatus.Authenticated) {
        if (isWelcome) {
          this.#matomo.trackEvent('Registration', 'Sign Up - SSO');
          this.#posthog.captureEvent('Sign Up', { type: 'SSO' });
          this.#mixpanel.addEvent('Sign Up', { type: 'SSO' });
        }

        // get return url from query parameters or default to home page
        // Note: Simple register pool will anyway redirect users on welcome after they finish
        const returnUrl = isWelcome ? AppRoutes.SimpleRegisterPoll : sanitizeReturnUrl(this.#activatedRoute.snapshot.queryParams.returnUrl ?? '/');

        if (returnUrl) this.#authService.navigateAfterLogin(returnUrl);
      }
    });
  }

  private setError(error?: string, errorMessage?: string): void {
    this.error = error;
    this.errorMessage = errorMessage;
  }

  private getUtmData(): UtmData {
    return {
      gclid: this.#cookie.get('gclid'),
      twclid: this.#cookie.get('twclid'),
      rdt_cid: this.#cookie.get('rdt_cid'),
      msclkid: this.#cookie.get('msclkid'),
      fbclid: this.#cookie.get('fbclid'),
      liclid: this.#cookie.get('liclid'),
      pk_campaign: this.#cookie.get('pk_campaign'),
      pk_keyword: this.#cookie.get('pk_keyword'),
      device: this.#cookie.get('device'),
      pk_medium: this.#cookie.get('pk_medium'),
      pk_source: this.#cookie.get('pk_source'),
      pk_cid: this.#cookie.get('pk_cid'),
      pk_buttonurl: this.#cookie.get('pk_buttonurl'),
      pk_buttonlocation: this.#cookie.get('pk_buttonlocation'),
      bunnyab: this.#cookie.get('bunnyab'),
      ref_domain: this.#cookie.get('ref_domain'),
      utm_blogs: this.#cookie.get('utm_blogs'),
      utm_academy: this.#cookie.get('utm_academy'),
      pk_ad: this.#cookie.get('pk_ad'),
      pk_copy: this.#cookie.get('pk_copy'),
      pk_url: this.#cookie.get('pk_url'),
      pk_placement: this.#cookie.get('pk_placement'),
      pk_platform: this.#cookie.get('pk_platform'),
    };
  }

  private setTenantUtmData(): void {
    if (!this.tenantId) return;

    const cookieOptions: CookieOptions = {
      domain: window.location.hostname,
      expires: DateTimeUtils.dayjsNow().add(1, 'year').format(),
    };

    if (!this.#cookie.get('pk_campaign')) this.#cookie.put('pk_campaign', this.tenantId, cookieOptions);
    if (!this.#cookie.get('pk_source')) this.#cookie.put('pk_source', this.tenantId, cookieOptions);
    if (!this.#cookie.get('pk_medium')) this.#cookie.put('pk_medium', this.tenantId, cookieOptions);
  }

  private initExtAuth(): void {
    this.#cookie.put(this.#appConfig.extAuthCookieName, JSON.stringify(this.#activatedRoute.snapshot.queryParams));

    this.isExtAuthMode = true;
    this.wpQueries = this.#activatedRoute.snapshot.queryParams;

    const url = this.wpQueries.callbackUrl;
    const domain = new URL(url).hostname;
    this.domainInfo = sanitizeReturnUrl(domain) ?? '';

    // Check potential phishing attempt
    if (this.wpQueries.source === 'discord') {
      const hasDiscordDomain = WHITE_LIST_RETURN_URL_DOMAINS.discord.some((discordDomain) => domain.endsWith(discordDomain));
      // Show warning if ?source=discord but domain is not in whitelist
      if (!hasDiscordDomain) this.unsafeDomainWarning = sanitizeReturnUrl(domain) || '';

      this.pageText = {
        description: `Connect Discord with bunny.net`,
        description2: `You will connect your Discord account to your bunny.net account at`,
        login: 'Log in And Connect',
        submit: 'Create an Account And Connect',
      };
    } else if (this.wpQueries.source === 'cli') {
      const hasCliDomain = WHITE_LIST_RETURN_URL_DOMAINS.cli.some((cliDomain) => domain.endsWith(cliDomain));
      // Show warning if ?source=cli but domain is not in whitelist
      if (!hasCliDomain) this.unsafeDomainWarning = sanitizeReturnUrl(domain) || '';
    } else if (this.wpQueries.source === 'wp-plugin') {
      // TODO: No checks for wp-plugin source, as we currently don't have a safe list
      this.pageText = {
        description: `WordPress Plugin Authorization`,
        description2: `You will authorize WordPress plugin at`,
        login: 'Log in And Authorize',
        submit: 'Create an Account And Authorize',
      };
      // unknown source but has returnUrl, show warning and generic text
    } else if (this.wpQueries.source) {
      // Generic warning for unknown sources, as we have no information about them, and they could be potential phishing attempts
      this.unsafeDomainWarning = sanitizeReturnUrl(domain) || '';
      this.pageText = {
        description: `Unknown Plugin Authorization`,
        description2: `You will authorize an unknown plugin at`,
        login: 'Log in And Authorize',
        submit: 'Create an Account And Authorize',
      };
    }
  }
}
